import { useCallback } from 'react';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGetPrivateShowcaseMenuOptions } from '~/hooks/useGetShowcaseMenuOptions';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import { canViewAsset } from '~/utils/permissions/assetPermissions';
import { canViewBoard } from '~/utils/permissions/boardPermissions';
import { canCreateCollection, canEditCollection } from '~/utils/permissions/workspacePermissions';

export type GetAddToShowcaseOptionParams = {
  assetIds: string[];
  boardIds: string[];
};

/**
 * Hook that returns a function to get the "Add to showcase" dropdown menu option.
 * Returns undefined if showcases are not enabled or if there are no showcase menu options.
 */
export const useAddToShowcaseOption = () => {
  const { collectionsEnabled } = useFeatureFlags();
  const { getShowcaseMenuOptions } = useGetPrivateShowcaseMenuOptions();

  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const assetPermissionCache = useAssetPermissionsCache();
  const boardPermissionCache = useBoardPermissionsCache();

  const getAddToShowcaseOption = useCallback(
    ({ assetIds, boardIds }: GetAddToShowcaseOptionParams): DropdownMenuOption | undefined => {
      const canAddToShowcase =
        assetIds.every((assetId) => canViewAsset(assetPermissionCache.getAssetPermissions(assetId))) &&
        boardIds.every((boardId) => canViewBoard(boardPermissionCache.getBoardPermissions(boardId))) &&
        (canEditCollection(workspacePermissions) || canCreateCollection(workspacePermissions));

      const options = getShowcaseMenuOptions({ assetIds, boardIds });

      if (collectionsEnabled && canAddToShowcase && options.length > 0) {
        return {
          ...getDropdownMenuOption('add-to-share-link'),
          // Ensures the submenu can expand in height after e.g. displaying no items from a search
          className: 'max-h-[400px]',
          options,
          type: 'sub',
        };
      }
    },
    [collectionsEnabled, getShowcaseMenuOptions, assetPermissionCache, boardPermissionCache, workspacePermissions],
  );

  if (!collectionsEnabled || !getShowcaseMenuOptions) {
    return undefined;
  }

  return getAddToShowcaseOption;
};
